import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {CRATER_PLUME_KIND} from '../../../configs/biomeEffects/craterPlume';
import ashfall from './ashfall';
import {
  TWO_PI,
  lerp,
  mixTints,
  randomIn,
  randomInt,
  randomRange,
  randomSign,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ДЫМ НАД КРАТЕРОМ (вулканическая подложка). Чистые функции без Phaser и DOM:
 * по этому описанию частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * Кратер привязан к нарисованному: точки — доли стороны картинки пейзажа.
 * Три его дорожки:
 *  - шлейф: клубы рождаются один за другим у основания нарисованного дыма и
 *    плывут по его ломаной — сначала быстро, потом все медленнее, — разбухая,
 *    светлея и тая. Каждый клуб — своя группа. Ветра матча шлейф не слушает —
 *    идет, как нарисован. Шлейф расходится КОНУСОМ: у жерла он узкий, а чем
 *    дальше, тем шире — и клубы крупнее, и расходятся они от оси дальше. Конус
 *    не уже нарисованного дыма (замер — в числах подложки): иначе нарисованный
 *    дым торчит по бокам анимированного неподвижной каймой;
 *  - свет: постоянная группа. Жерло и огоньки на кромке кратера дышат неровно;
 *  - выдох: изредка жерло разгорается, и из него за несколько секунд выходит
 *    горсть клубов крупнее и темнее обычных. Расходятся они тем же конусом.
 *
 * Дым лежит на земле (слой GROUND), свет — там же, но светится (GROUND_GLOW):
 * жетоны и отряды их закрывают, а угол картинки гасит, как нарисованный дым.
 *
 * Четвертая дорожка — пепел над полем: построитель пепельной равнины
 * (./ashfall) со своими числами. Кратер лежит за углом поля, а пепел идет там,
 * куда смотрит камера. Его несет ветер матча — на вулкане он закреплен слева
 * направо (configs/fieldAmbience), как нарисованный шлейф.
 *
 * Дорожки кратера помечены usesWind: false: наклон ветра у каждой партии свой,
 * и при его смене на старте партии перестраивается только пепел.
 */

const PUFF = 'puff';
const GLOW = 'glow';
const FLARE = 'flare';

const createTracks = (context, params) => {
  const {side, hexSize} = context;
  const {plume, glow, exhale} = params;
  const cellWidth = hexSize * 2;

  // Ломаная нарисованного шлейфа в координатах мира и длина пути до каждой ее точки
  const points = params.path.map(({u, v}) => context.toWorld(u, v));
  const distances = [0];
  for (let index = 1; index < points.length; index += 1) {
    const [from, to] = [points[index - 1], points[index]];
    distances.push(distances[index - 1] + Math.hypot(to.x - from.x, to.y - from.y));
  }
  const pathLength = distances[distances.length - 1];
  // Поперек шлейфа — поперек прямой от его начала к концу: на изломах ломаной
  // клуб с большим сдвигом вбок не дергается. Так же мерян и нарисованный дым
  const start = points[0];
  const end = points[points.length - 1];
  const span = Math.hypot(end.x - start.x, end.y - start.y);
  const across = {x: -(end.y - start.y) / span, y: (end.x - start.x) / span};

  // Точка шлейфа пишется в один и тот же объект: расчет идет каждый кадр на каждый клуб
  const pathPoint = {x: 0, y: 0};
  /** Точка ломаной на доле ее длины (0..1) */
  const resolvePathPoint = (share) => {
    const distance = Math.min(1, Math.max(0, share)) * pathLength;
    let index = 1;
    while (index < points.length - 1 && distances[index] < distance) {
      index += 1;
    }
    const segmentShare = (distance - distances[index - 1]) / (distances[index] - distances[index - 1]);
    pathPoint.x = lerp(points[index - 1].x, points[index].x, segmentShare);
    pathPoint.y = lerp(points[index - 1].y, points[index].y, segmentShare);
    return pathPoint;
  };

  /** Клуб дыма: обычный или клуб выдоха (крупнее, плотнее, темнее и с задержкой) */
  const buildPuff = (rng, {delayMs = 0, sizeScale = 1, alpha = plume.alpha, youngTint = plume.tints.young} = {}) => ({
    kind: PUFF,
    layer: FIELD_BIOME_LAYER_KEYS.GROUND,
    texture: FIELD_BIOME_TEXTURES.PUFF,
    tint: youngTint,
    youngTint,
    delayMs,
    lifeMs: randomIn(rng, plume.lifeMs),
    // Место клуба поперек конуса шлейфа: от одного его края (-1) до другого
    // (1), одно на всю жизнь — клуб уходит от жерла своим лучом. Места —
    // равномерно по ширине: будь разброс кругом, у краев конуса клубы шли бы
    // в разы реже, чем посередине, и нарисованный дым там стоял бы без движения
    lateral: randomRange(rng, -1, 1),
    sizeScale: sizeScale * randomIn(rng, plume.sizeJitter),
    alpha,
    swayMs: randomIn(rng, plume.swayMs),
    swayPhase: rng() * TWO_PI,
    startRad: rng() * TWO_PI,
    // Кружится медленно, в любую сторону
    spinRadPerMs: (toRadians(plume.spinDegPerSecond) / 1000) * randomRange(rng, 0.3, 1) * randomSign(rng),
  });

  const resolvePuffPose = (particle, elapsedMs, out) => {
    const life = (elapsedMs - particle.delayMs) / particle.lifeMs;
    if (!(life > 0) || !(life < 1)) {
      return writeHiddenPose(out);
    }
    // Быстро из жерла, дальше — медленный дрейф
    const progress = 1 - (1 - life) ** plume.progressPower;
    const point = resolvePathPoint(progress);
    const size = Math.min(
      cellWidth * plume.sizeCells.max,
      Math.max(cellWidth * plume.sizeCells.min, side * lerp(plume.sizeShare.from, plume.sizeShare.to, progress))
    ) * particle.sizeScale;
    // Конус: чем дальше клуб ушел от жерла, тем дальше он от оси шлейфа
    const scatter = side * lerp(plume.scatterShare.from, plume.scatterShare.to, progress);
    const sway = size * plume.swayShare * progress
      * Math.sin((TWO_PI * elapsedMs) / particle.swayMs + particle.swayPhase);
    const offset = particle.lateral * scatter + sway;
    writePose(
      out,
      point.x + across.x * offset,
      point.y + across.y * offset,
      particle.alpha * smoothstep(0, plume.fadeInShare, life) * (1 - life) ** plume.fadePower,
      size, size,
      particle.startRad + particle.spinRadPerMs * elapsedMs
    );
    // У жерла дым темнее, дальше светлеет
    const {old, turnFrom, turnTo} = plume.tints;
    out.tint = mixTints(particle.youngTint, old, smoothstep(turnFrom, turnTo, life));
    return out;
  };

  /** Пятно света на своем месте картинки */
  const buildSpot = (kind, spot) => {
    const {x, y} = context.toWorld(spot.u, spot.v);
    return {
      kind,
      layer: FIELD_BIOME_LAYER_KEYS.GROUND_GLOW,
      sublayer: spot.sublayer,
      texture: FIELD_BIOME_TEXTURES.DOT,
      tint: spot.tint,
      x,
      y,
      size: side * spot.sizeShare,
      alpha: spot.alpha,
    };
  };

  // -------------------------------------------------------------------- шлейф

  const plumeTrack = {
    usesWind: false,
    schedule: {
      firstDelayMs: plume.firstDelayMs, intervalMs: plume.intervalMs, maxGroups: plume.maxGroups,
      prewarm: plume.prewarm,
    },
    buildGroup: ({rng}) => {
      const puff = buildPuff(rng);
      return {durationMs: puff.lifeMs, particles: [puff]};
    },
    resolvePose: (group, particle, elapsedMs, out) => resolvePuffPose(particle, elapsedMs, out),
  };

  // --------------------------------------------------------------------- свет

  const glowTrack = {
    usesWind: false,
    schedule: {standing: true},
    buildGroup: ({rng}) => {
      // Пятна одного очага (beat) дышат в такт, разные очаги — вразнобой
      const beats = {};
      const particles = glow.spots.map((spot) => {
        if (!beats[spot.beat]) {
          beats[spot.beat] = {
            breathMs: randomIn(rng, glow.breathMs), phase: rng() * TWO_PI, secondPhase: rng() * TWO_PI,
          };
        }
        return {...buildSpot(GLOW, spot), ...beats[spot.beat]};
      });
      return {durationMs: Infinity, particles};
    },
    resolvePose: (group, particle, elapsedMs, out) => {
      // Неровное дыхание: две волны с несовпадающими периодами
      const turn = (TWO_PI * elapsedMs) / particle.breathMs;
      const breath = 0.5 + 0.5 * (
        (1 - glow.secondMix) * Math.sin(turn + particle.phase)
        + glow.secondMix * Math.sin(turn / glow.secondPeriodShare + particle.secondPhase)
      );
      const alpha = lerp(particle.alpha.min, particle.alpha.max, breath);
      return writePose(out, particle.x, particle.y, alpha, particle.size, particle.size, 0);
    },
  };

  // -------------------------------------------------------------------- выдох

  const exhaleTrack = {
    usesWind: false,
    schedule: {firstDelayMs: exhale.firstDelayMs, intervalMs: exhale.intervalMs, maxGroups: exhale.maxGroups},
    buildGroup: ({rng}) => {
      const particles = exhale.flare.spots.map((spot) => buildSpot(FLARE, spot));
      const puffCount = randomInt(rng, exhale.puffCount.min, exhale.puffCount.max);
      let durationMs = exhale.burstMs + exhale.flare.decayMs;
      for (let index = 0; index < puffCount; index += 1) {
        const puff = buildPuff(rng, {
          // Клубы выходят один за другим на протяжении выброса
          delayMs: (exhale.burstMs * (index + rng())) / puffCount,
          sizeScale: exhale.sizeScale,
          alpha: exhale.alpha,
          youngTint: exhale.youngTint,
        });
        particles.push(puff);
        durationMs = Math.max(durationMs, puff.delayMs + puff.lifeMs);
      }
      return {durationMs, particles};
    },
    resolvePose: (group, particle, elapsedMs, out) => {
      if (particle.kind === PUFF) {
        return resolvePuffPose(particle, elapsedMs, out);
      }
      // Вспышка жерла: разгорается, держится весь выброс и гаснет
      const flare = smoothstep(0, exhale.flare.riseMs, elapsedMs)
        * (1 - smoothstep(exhale.burstMs, exhale.burstMs + exhale.flare.decayMs, elapsedMs));
      if (!(flare > 0)) {
        return writeHiddenPose(out);
      }
      const size = particle.size * lerp(exhale.flare.startScale, 1, flare);
      return writePose(out, particle.x, particle.y, particle.alpha * flare, size, size, 0);
    },
  };

  return [plumeTrack, glowTrack, exhaleTrack, ...ashfall.createTracks(context, params.ashfall)];
};

const craterPlume = {
  kind: CRATER_PLUME_KIND,
  textures: [...new Set([FIELD_BIOME_TEXTURES.PUFF, FIELD_BIOME_TEXTURES.DOT, ...ashfall.textures])],
  createTracks,
};

export default craterPlume;
